import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { useWS } from '../contexts/WebSocketContext';
import { useAuth } from '../contexts/AuthContext';
import ConversationList from '../components/communications/ConversationList';
import MessageThread from '../components/communications/MessageThread';
import ComposeMessage from '../components/communications/ComposeMessage';
import {
  MessageSquare, Search, Filter, Phone, Mail, MessageCircle,
  MoreVertical, Users, Briefcase, Tag, Clock, CheckCircle, AlertCircle,
  ChevronRight, PhoneCall, Voicemail, Send, Inbox, Trash2, Target,
  Play, Pause, User, Eye, RefreshCw, Star
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Communications = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const ws = useWS();
  
  // State
  const [conversations, setConversations] = useState([]);
  const [emailThreads, setEmailThreads] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedEmailThread, setSelectedEmailThread] = useState(null);
  const [selectedEmailMessages, setSelectedEmailMessages] = useState([]);
  const [conversationData, setConversationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailLoading, setEmailLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [bucketFilter, setBucketFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all'); // 'all', 'sms', 'email'
  
  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Get job_id from URL if present
  const jobIdFromUrl = searchParams.get('job_id');
  
  // Prime Goals state
  const [primeGoals, setPrimeGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  
  // Prime Reply state
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedGoalForReply, setSelectedGoalForReply] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyAction, setReplyAction] = useState('continue');
  const [sendingReply, setSendingReply] = useState(false);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      
      // Handle Prime-specific filters
      if (bucketFilter === 'prime_managed') {
        params.append('is_prime_managed', 'true');
      } else if (bucketFilter === 'prime_goals') {
        params.append('thread_type', 'PRIME_GOAL');
      } else if (bucketFilter !== 'all') {
        params.append('bucket', bucketFilter);
      }
      
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (jobIdFromUrl) params.append('job_id', jobIdFromUrl);
      
      const response = await axios.get(`${API}/communications/conversations?${params}`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [bucketFilter, statusFilter, jobIdFromUrl]);
  
  // Fetch Prime Goals
  const fetchPrimeGoals = useCallback(async () => {
    if (bucketFilter !== 'prime_goals') return;
    
    setLoadingGoals(true);
    try {
      const response = await axios.get(`${API}/prime/goals/list`);
      setPrimeGoals(response.data.goals || []);
    } catch (error) {
      console.error('Error fetching prime goals:', error);
    } finally {
      setLoadingGoals(false);
    }
  }, [bucketFilter]);
  
  // Handle Super Admin reply to Prime
  const handleReplyToPrime = async () => {
    if (!selectedGoalForReply || !replyText.trim()) return;
    
    setSendingReply(true);
    try {
      await axios.post(`${API}/prime/goals/super-admin/reply`, {
        goal_id: selectedGoalForReply.id,
        response: replyText,
        action: replyAction
      });
      
      toast.success(
        replyAction === 'continue' ? 'Response sent to Prime' :
        replyAction === 'pause' ? 'Goal paused' :
        replyAction === 'cancel' ? 'Goal cancelled' :
        replyAction === 'escalate' ? 'Goal escalated' : 'Done'
      );
      
      setShowReplyModal(false);
      setSelectedGoalForReply(null);
      setReplyText('');
      setReplyAction('continue');
      fetchPrimeGoals();
    } catch (error) {
      console.error('Error replying to Prime:', error);
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };
  
  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/communications/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);
  
  // Fetch email threads
  const fetchEmailThreads = useCallback(async () => {
    if (channelFilter === 'sms') return; // Skip if SMS only
    
    setEmailLoading(true);
    try {
      const params = { user_id: user?.id, folder: 'INBOX', page: 1, page_size: 50 };
      if (searchTerm) params.search = searchTerm;
      
      const response = await axios.get(`${API}/email/inbox`, { params });
      setEmailThreads(response.data.threads || []);
    } catch (error) {
      console.error('Error fetching email threads:', error);
      // Don't show error toast - emails might not be set up yet
      setEmailThreads([]);
    } finally {
      setEmailLoading(false);
    }
  }, [user?.id, channelFilter, searchTerm]);
  
  // Fetch email stats
  const fetchEmailStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/email/stats`, { params: { user_id: user?.id } });
      setEmailStats(response.data);
    } catch (error) {
      console.error('Error fetching email stats:', error);
    }
  }, [user?.id]);
  
  // Fetch single email thread with messages
  const fetchEmailThreadDetails = useCallback(async (threadId) => {
    setMessagesLoading(true);
    try {
      const response = await axios.get(`${API}/email/threads/${threadId}`, { params: { user_id: user?.id } });
      setSelectedEmailThread(response.data.thread);
      setSelectedEmailMessages(response.data.messages || []);
      
      // Update local email threads to mark as read
      setEmailThreads(prev => prev.map(t => 
        t.id === threadId ? { ...t, is_unread: false } : t
      ));
    } catch (error) {
      console.error('Error fetching email thread:', error);
      toast.error('Failed to load email thread');
    } finally {
      setMessagesLoading(false);
    }
  }, [user?.id]);
  
  // Fetch single conversation with messages
  const fetchConversationDetails = useCallback(async (conversationId) => {
    setMessagesLoading(true);
    try {
      const response = await axios.get(`${API}/communications/conversations/${conversationId}`);
      setConversationData(response.data);
      
      // Mark as read
      await axios.post(`${API}/communications/conversations/${conversationId}/mark-read`);
      
      // Update local state
      setConversations(prev => prev.map(c => 
        c.id === conversationId ? { ...c, unread_count: 0 } : c
      ));
    } catch (error) {
      console.error('Error fetching conversation:', error);
      toast.error('Failed to load conversation');
    } finally {
      setMessagesLoading(false);
    }
  }, []);
  
  // Delete conversation
  const handleDeleteConversation = async () => {
    if (!conversationToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/communications/conversations/${conversationToDelete}`);
      
      toast.success('Conversation deleted');
      
      // Clear selection if deleted conversation was selected
      if (selectedConversation === conversationToDelete) {
        setSelectedConversation(null);
        setConversationData(null);
      }
      
      // Refresh conversations list
      fetchConversations();
      fetchStats();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      toast.error('Failed to delete conversation');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setConversationToDelete(null);
    }
  };
  
  // Initial load
  useEffect(() => {
    fetchConversations();
    fetchStats();
    fetchEmailStats();
    
    // Check URL params for pre-selected conversation
    const convId = searchParams.get('conversation');
    if (convId) {
      setSelectedConversation(convId);
      fetchConversationDetails(convId);
    }
  }, [fetchConversations, fetchStats, fetchEmailStats, searchParams, fetchConversationDetails]);
  
  // Fetch email threads when channel filter changes
  useEffect(() => {
    if (channelFilter !== 'sms') {
      fetchEmailThreads();
    }
  }, [channelFilter, fetchEmailThreads]);
  
  // Fetch Prime Goals when filter changes
  useEffect(() => {
    if (bucketFilter === 'prime_goals') {
      fetchPrimeGoals();
    }
  }, [bucketFilter, fetchPrimeGoals]);
  
  // WebSocket message handling
  useEffect(() => {
    if (!ws.lastMessage) return;
    
    const { type, conversation_id, message } = ws.lastMessage;
    
    if (type === 'new_message') {
      // Update conversation list
      setConversations(prev => {
        const idx = prev.findIndex(c => c.id === conversation_id);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            last_message: message?.body?.substring(0, 100),
            last_message_at: message?.created_at,
            unread_count: conversation_id === selectedConversation 
              ? 0 
              : (updated[idx].unread_count || 0) + 1
          };
          // Move to top
          updated.unshift(updated.splice(idx, 1)[0]);
          return updated;
        }
        return prev;
      });
      
      // Update current conversation if selected
      if (conversation_id === selectedConversation && conversationData) {
        setConversationData(prev => ({
          ...prev,
          messages: [...(prev.messages || []), { ...message, item_type: 'message' }]
        }));
      }
    }
  }, [ws.lastMessage, selectedConversation, conversationData]);
  
  // Handle conversation selection
  const handleSelectConversation = (convId) => {
    // Clear email selection when selecting SMS conversation
    setSelectedEmailThread(null);
    setSelectedEmailMessages([]);
    
    setSelectedConversation(convId);
    setSearchParams({ conversation: convId });
    fetchConversationDetails(convId);
  };
  
  // Handle email thread selection
  const handleSelectEmailThread = (threadId) => {
    // Clear SMS conversation selection when selecting email
    setSelectedConversation(null);
    setConversationData(null);
    
    setSearchParams({ email_thread: threadId });
    fetchEmailThreadDetails(threadId);
  };
  
  // Handle send message
  const handleSendMessage = async (messageData) => {
    if (!selectedConversation) return;
    
    try {
      await axios.post(
        `${API}/communications/conversations/${selectedConversation}/messages`,
        messageData
      );
      
      // Refresh conversation
      fetchConversationDetails(selectedConversation);
      fetchStats();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };
  
  // Filter conversations by search
  const filteredConversations = conversations.filter(conv => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      conv.customer_name?.toLowerCase().includes(search) ||
      conv.job_title?.toLowerCase().includes(search) ||
      conv.last_message?.toLowerCase().includes(search)
    );
  });
  
  // Filter email threads by search
  const filteredEmailThreads = emailThreads.filter(thread => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      thread.subject?.toLowerCase().includes(search) ||
      thread.snippet?.toLowerCase().includes(search) ||
      thread.participants?.some(p => 
        p.name?.toLowerCase().includes(search) || 
        p.email?.toLowerCase().includes(search)
      )
    );
  });
  
  // Transform email threads to unified conversation format for mixed view
  const emailAsConversations = filteredEmailThreads.map(thread => ({
    id: `email_${thread.id}`,
    original_id: thread.id,
    type: 'email',
    customer_name: thread.participants?.[0]?.name || thread.participants?.[0]?.email || 'Unknown',
    customer_email: thread.participants?.[0]?.email,
    last_message: thread.snippet,
    last_message_at: thread.last_message_date,
    last_message_channel: 'email',
    unread_count: thread.is_unread ? 1 : 0,
    is_starred: thread.is_starred,
    subject: thread.subject,
    bucket: 'email',
    status: thread.is_unread ? 'open' : 'closed'
  }));
  
  // Get combined list based on channel filter
  const getDisplayList = () => {
    if (channelFilter === 'email') {
      return emailAsConversations;
    }
    if (channelFilter === 'sms') {
      return filteredConversations;
    }
    // 'all' - merge and sort by date
    const combined = [...filteredConversations.map(c => ({ ...c, type: 'sms' })), ...emailAsConversations];
    return combined.sort((a, b) => {
      const dateA = new Date(a.last_message_at || 0);
      const dateB = new Date(b.last_message_at || 0);
      return dateB - dateA;
    });
  };
  
  const displayList = bucketFilter === 'prime_goals' ? [] : getDisplayList();
  
  // Handle unified item selection
  const handleSelectItem = (item) => {
    if (item.type === 'email') {
      handleSelectEmailThread(item.original_id);
    } else {
      handleSelectConversation(item.id);
    }
  };
  
  // Get channel icon
  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'sms': return <MessageCircle className="h-4 w-4" />;
      case 'email': return <Mail className="h-4 w-4" />;
      case 'call': return <PhoneCall className="h-4 w-4" />;
      case 'voicemail': return <Voicemail className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };
  
  return (
    <Layout>
      <div className="h-[calc(100vh-140px)] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <MessageSquare className="h-7 w-7 text-primary" />
              Communications Hub
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Unified inbox for all customer communications
            </p>
          </div>
          
          {/* Stats Badges */}
          {stats && (
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="gap-1">
                <Inbox className="h-3 w-3" />
                {stats.open_conversations} Open
              </Badge>
              <Badge variant="outline" className="gap-1 text-yellow-600">
                <Clock className="h-3 w-3" />
                {stats.waiting_conversations} Waiting
              </Badge>
              <Badge variant="outline" className="gap-1 text-green-600">
                <CheckCircle className="h-3 w-3" />
                {stats.closed_conversations} Closed
              </Badge>
              {stats.unread_messages > 0 && (
                <Badge className="bg-red-500">
                  {stats.unread_messages} Unread
                </Badge>
              )}
              {emailStats && emailStats.unread_threads > 0 && (
                <Badge className="bg-blue-500 gap-1">
                  <Mail className="h-3 w-3" />
                  {emailStats.unread_threads} Emails
                </Badge>
              )}
            </div>
          )}
        </div>
        
        {/* Main Content - Two Panel Layout */}
        <div className="flex-1 flex gap-4 min-h-0">
          {/* Left Panel - Conversation List */}
          <Card className="w-[400px] flex flex-col">
            <CardHeader className="pb-2">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              {/* Filters */}
              <div className="flex gap-2 mt-2">
                <Select value={bucketFilter} onValueChange={setBucketFilter}>
                  <SelectTrigger className="flex-1 h-8">
                    <SelectValue placeholder="Bucket" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Buckets</SelectItem>
                    <SelectItem value="lead_intake">Lead Intake</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="flex-1 h-8">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="waiting">Waiting</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Channel Tabs - SMS / Email */}
              <Tabs value={channelFilter} onValueChange={setChannelFilter} className="mt-2">
                <TabsList className="w-full grid grid-cols-3 h-9">
                  <TabsTrigger value="all" className="text-xs gap-1.5">
                    <Inbox className="h-3.5 w-3.5" />
                    All
                    {(stats?.unread_messages || 0) + (emailStats?.unread_threads || 0) > 0 && (
                      <Badge variant="secondary" className="h-4 px-1 text-[10px] ml-1">
                        {(stats?.unread_messages || 0) + (emailStats?.unread_threads || 0)}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="sms" className="text-xs gap-1.5">
                    <MessageCircle className="h-3.5 w-3.5" />
                    SMS
                    {stats?.unread_messages > 0 && (
                      <Badge variant="secondary" className="h-4 px-1 text-[10px] ml-1">
                        {stats.unread_messages}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="email" className="text-xs gap-1.5">
                    <Mail className="h-3.5 w-3.5" />
                    Email
                    {emailStats?.unread_threads > 0 && (
                      <Badge variant="secondary" className="h-4 px-1 text-[10px] ml-1">
                        {emailStats.unread_threads}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              
              {/* Bucket Tabs - Only show for SMS channel */}
              {channelFilter !== 'email' && (
                <Tabs value={bucketFilter} onValueChange={setBucketFilter} className="mt-2">
                  <TabsList className="w-full grid grid-cols-7 h-8">
                    <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
                    <TabsTrigger value="lead_intake" className="text-xs">Leads</TabsTrigger>
                    <TabsTrigger value="production" className="text-xs">Production</TabsTrigger>
                    <TabsTrigger value="service" className="text-xs">Service</TabsTrigger>
                    <TabsTrigger value="marketing" className="text-xs">Marketing</TabsTrigger>
                    <TabsTrigger value="prime_managed" className="text-xs flex items-center gap-1">
                      <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                      Prime
                    </TabsTrigger>
                    <TabsTrigger value="prime_goals" className="text-xs flex items-center gap-1">
                      <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                      Goals
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
            </CardHeader>
            
            <CardContent className="flex-1 overflow-hidden p-0">
              {bucketFilter === 'prime_goals' ? (
                /* Prime Goals Panel */
                <ScrollArea className="h-full">
                  <div className="p-4 space-y-3">
                    {loadingGoals ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                        Loading goals...
                      </div>
                    ) : primeGoals.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">No active Prime goals</p>
                        <p className="text-xs mt-1">Goals created by Prime will appear here</p>
                      </div>
                    ) : (
                      primeGoals.map((goal) => (
                        <Card 
                          key={goal.id} 
                          className={`cursor-pointer hover:shadow-md transition-shadow ${
                            goal.status === 'COMPLETED' ? 'opacity-60' : ''
                          } ${goal.status === 'ESCALATED' ? 'border-red-200' : 'border-purple-200'}`}
                          onClick={() => {
                            if (goal.thread_id) {
                              setSelectedConversation(goal.thread_id);
                              fetchConversationDetails(goal.thread_id);
                            }
                          }}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Target className="h-4 w-4 text-purple-500" />
                                <span className="font-medium text-sm truncate">{goal.goal_title}</span>
                              </div>
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${
                                  goal.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                                  goal.status === 'ESCALATED' ? 'bg-red-100 text-red-800' :
                                  goal.status === 'WAITING_CUSTOMER' ? 'bg-yellow-100 text-yellow-800' :
                                  goal.status === 'WAITING_SUPER_ADMIN' ? 'bg-orange-100 text-orange-800' :
                                  goal.status === 'PAUSED' ? 'bg-gray-100 text-gray-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}
                              >
                                {goal.status?.replace('_', ' ')}
                              </Badge>
                            </div>
                            
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                              <User className="h-3 w-3" />
                              <span>{goal.recipient_name}</span>
                              {goal.recipient_phone && (
                                <>
                                  <span>•</span>
                                  <Phone className="h-3 w-3" />
                                  <span>{goal.recipient_phone}</span>
                                </>
                              )}
                            </div>
                            
                            <div className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                <span>Attempt {goal.current_attempt || 1} of {goal.max_attempts || 3}</span>
                              </div>
                              
                              <div className="flex gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 px-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (goal.thread_id) {
                                      setSelectedConversation(goal.thread_id);
                                      fetchConversationDetails(goal.thread_id);
                                    }
                                  }}
                                >
                                  <Eye className="h-3 w-3" />
                                </Button>
                                {goal.status !== 'COMPLETED' && goal.status !== 'CANCELLED' && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-6 px-2"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const endpoint = goal.status === 'PAUSED' ? 'resume' : 'pause';
                                        await axios.post(`${API}/prime/goals/${goal.id}/${endpoint}`);
                                        toast.success(goal.status === 'PAUSED' ? 'Goal resumed' : 'Goal paused');
                                        fetchPrimeGoals();
                                      } catch (error) {
                                        toast.error('Failed to update goal');
                                      }
                                    }}
                                  >
                                    {goal.status === 'PAUSED' ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
                                  </Button>
                                )}
                              </div>
                            </div>
                            
                            {goal.next_followup_at && goal.status === 'WAITING_CUSTOMER' && (
                              <div className="mt-2 pt-2 border-t text-xs text-muted-foreground">
                                Next follow-up: {new Date(goal.next_followup_at).toLocaleString()}
                              </div>
                            )}
                            
                            {/* Prime needs input alert */}
                            {goal.status === 'WAITING_SUPER_ADMIN' && (
                              <div className="mt-2 pt-2 border-t">
                                <div className="flex items-center gap-2 text-xs text-orange-600 dark:text-orange-400 mb-2">
                                  <AlertCircle className="h-3 w-3" />
                                  <span className="font-medium">Prime needs your input</span>
                                </div>
                                <Button
                                  size="sm"
                                  className="w-full h-7 text-xs bg-orange-600 hover:bg-orange-700"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedGoalForReply(goal);
                                    setShowReplyModal(true);
                                  }}
                                >
                                  <Send className="h-3 w-3 mr-1" />
                                  Reply to Prime
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </ScrollArea>
              ) : (
                <ConversationList
                  conversations={displayList}
                  selectedId={selectedConversation || (selectedEmailThread ? `email_${selectedEmailThread.id}` : null)}
                  onSelect={handleSelectItem}
                  loading={loading || emailLoading}
                  getChannelIcon={getChannelIcon}
                />
              )}
            </CardContent>
          </Card>
          
          {/* Right Panel - Conversation Thread */}
          <Card className="flex-1 flex flex-col">
            {selectedConversation && conversationData ? (
              <>
                {/* SMS Conversation Header */}
                <CardHeader className="border-b pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback>
                          {conversationData.customer?.first_name?.[0] || 'U'}
                          {conversationData.customer?.last_name?.[0] || ''}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-semibold flex items-center gap-2">
                          {conversationData.customer?.first_name} {conversationData.customer?.last_name}
                          {conversationData.job && (
                            <Badge variant="outline" className="ml-2">
                              <Briefcase className="h-3 w-3 mr-1" />
                              {conversationData.job.title}
                            </Badge>
                          )}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          {conversationData.customer?.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {conversationData.customer.phone}
                            </span>
                          )}
                          {conversationData.customer?.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {conversationData.customer.email}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge className={`
                        ${conversationData.conversation?.bucket === 'lead_intake' ? 'bg-blue-500' : ''}
                        ${conversationData.conversation?.bucket === 'production' ? 'bg-orange-500' : ''}
                        ${conversationData.conversation?.bucket === 'service' ? 'bg-purple-500' : ''}
                        ${conversationData.conversation?.bucket === 'marketing' ? 'bg-green-500' : ''}
                      `}>
                        {conversationData.conversation?.bucket || 'general'}
                      </Badge>
                      <Badge variant={conversationData.conversation?.status === 'open' ? 'default' : 'secondary'}>
                        {conversationData.conversation?.status || 'open'}
                      </Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setConversationToDelete(selectedConversation);
                              setShowDeleteConfirm(true);
                            }}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Conversation
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  
                  {/* Quick Actions */}
                  {conversationData.job && (
                    <div className="mt-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/operations-hub/jobs/${conversationData.job.id}`)}
                      >
                        <Briefcase className="h-3 w-3 mr-1" />
                        View Job
                        <ChevronRight className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                  )}
                </CardHeader>
                
                {/* Message Thread */}
                <MessageThread
                  messages={conversationData.messages || []}
                  loading={messagesLoading}
                  customer={conversationData.customer}
                />
                
                {/* Compose Message */}
                <ComposeMessage
                  onSend={handleSendMessage}
                  conversationId={selectedConversation}
                  customer={conversationData.customer}
                />
              </>
            ) : selectedEmailThread ? (
              <>
                {/* Email Thread Header */}
                <CardHeader className="border-b pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{selectedEmailThread.subject}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                          <span>{selectedEmailThread.participants?.[0]?.name || selectedEmailThread.participants?.[0]?.email}</span>
                          <span>•</span>
                          <span>{selectedEmailThread.message_count} message(s)</span>
                          {selectedEmailThread.is_starred && (
                            <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-500">Email</Badge>
                      <Badge variant={selectedEmailThread.is_unread ? 'default' : 'secondary'}>
                        {selectedEmailThread.is_unread ? 'Unread' : 'Read'}
                      </Badge>
                      {selectedEmailThread.linked_contact_name && (
                        <Badge variant="outline" className="gap-1">
                          <User className="h-3 w-3" />
                          {selectedEmailThread.linked_contact_name}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                
                {/* Email Messages */}
                <ScrollArea className="flex-1 p-4">
                  {messagesLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {selectedEmailMessages.map((message) => (
                        <Card key={message.id} className="bg-slate-50/50 dark:bg-slate-900/50">
                          <CardHeader className="pb-2">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-9 w-9">
                                <AvatarFallback className="text-xs">
                                  {(message.from_participant?.name || message.from_participant?.email || 'U')
                                    .split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className="font-medium text-sm truncate">
                                    {message.from_participant?.name || message.from_participant?.email}
                                  </p>
                                  <span className="text-xs text-muted-foreground">
                                    {new Date(message.date).toLocaleString()}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                  to {message.to_participants?.map(p => p.name || p.email).join(', ')}
                                </p>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div 
                              className="prose prose-sm dark:prose-invert max-w-none text-sm"
                              dangerouslySetInnerHTML={{ 
                                __html: message.body_html || `<p>${message.body_text || message.snippet}</p>` 
                              }}
                            />
                            {message.has_attachments && message.attachments?.length > 0 && (
                              <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                                {message.attachments.map(att => (
                                  <div key={att.id} className="flex items-center gap-2 px-2 py-1 bg-slate-100 dark:bg-slate-800 rounded text-xs">
                                    <Tag className="h-3 w-3 text-muted-foreground" />
                                    <span className="truncate max-w-[120px]">{att.filename}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </ScrollArea>
                
                {/* Email Quick Actions */}
                <div className="p-3 border-t bg-slate-50/50 dark:bg-slate-900/50">
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => navigate('/email')}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Open in Email Hub
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center">
                <div>
                  <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-medium text-muted-foreground">Select a conversation</h3>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    Choose a conversation from the list to view messages
                  </p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this conversation and all its messages. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConversation}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Reply to Prime Modal */}
      <Dialog open={showReplyModal} onOpenChange={setShowReplyModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-500" />
              Reply to Prime
            </DialogTitle>
            <DialogDescription>
              {selectedGoalForReply && (
                <>
                  <span className="font-medium text-foreground">{selectedGoalForReply.goal_title}</span>
                  <br />
                  <span className="text-muted-foreground">Contact: {selectedGoalForReply.recipient_name}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Your Response</label>
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Tell Prime how to proceed with this goal..."
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Action</label>
              <Select value={replyAction} onValueChange={setReplyAction}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="continue">
                    <span className="flex items-center gap-2">
                      <Play className="h-3 w-3 text-green-500" />
                      Continue - Prime will send a follow-up based on your guidance
                    </span>
                  </SelectItem>
                  <SelectItem value="pause">
                    <span className="flex items-center gap-2">
                      <Pause className="h-3 w-3 text-yellow-500" />
                      Pause - Stop follow-ups for now
                    </span>
                  </SelectItem>
                  <SelectItem value="cancel">
                    <span className="flex items-center gap-2">
                      <Trash2 className="h-3 w-3 text-red-500" />
                      Cancel - End this goal
                    </span>
                  </SelectItem>
                  <SelectItem value="escalate">
                    <span className="flex items-center gap-2">
                      <AlertCircle className="h-3 w-3 text-orange-500" />
                      Escalate - Mark as needing manual intervention
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReplyModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReplyToPrime}
              disabled={sendingReply || !replyText.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {sendingReply ? 'Sending...' : 'Send to Prime'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Communications;